import { Component } from "react";
import {
  ActionIcon,
  Button,
  Center,
  Code,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconCopy,
  IconRefresh,
  IconReload,
} from "@tabler/icons-react";

// Beyaz sayfa yerine toparlanabilir bir hata ekranı. "Tekrar Dene" full-refresh
// yapmadan alt ağacı yeniden dener (geçici/render race'lerinde iş kaybı olmaz);
// deterministik hata sürerse "Sayfayı Yenile" kalır. Tam stack console'da; kullanıcı
// mesajı tek tıkla kopyalayıp iletebilir.
export class ErrorBoundary extends Component {
  state = { error: null, resetKey: 0 };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[panel] beklenmeyen hata", error, info.componentStack);
  }

  retry = () =>
    this.setState((prev) => ({ error: null, resetKey: prev.resetKey + 1 }));

  render() {
    if (!this.state.error) {
      return <div key={this.state.resetKey}>{this.props.children}</div>;
    }

    const message = String(this.state.error?.message ?? this.state.error);

    return (
      <Center mih="100dvh" p="md">
        <Paper withBorder radius="lg" p="xl" maw={560} w="100%" shadow="sm">
          <Stack gap="md" align="center">
            <ThemeIcon size={56} radius="xl" variant="light" color="red">
              <IconAlertTriangle size={30} stroke={1.6} />
            </ThemeIcon>
            <Stack gap={4} align="center">
              <Text fw={700} size="lg">
                Bir şeyler ters gitti
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Önce &quot;Tekrar Dene&quot;yi dene. Sürerse aşağıdaki mesajı
                bize ilet — hızlıca düzeltiriz.
              </Text>
            </Stack>

            <Paper
              withBorder
              radius="md"
              bg="var(--mantine-color-gray-0)"
              p="xs"
              w="100%"
            >
              <Group
                justify="space-between"
                wrap="nowrap"
                align="flex-start"
                gap="xs"
              >
                <Code
                  block
                  style={{
                    background: "transparent",
                    maxHeight: 160,
                    overflow: "auto",
                    flex: 1,
                  }}
                >
                  {message}
                </Code>
                <CopyButton value={message} timeout={1500}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? "Kopyalandı" : "Kopyala"}
                      withArrow
                    >
                      <ActionIcon
                        aria-label="Hata ayrıntısını kopyala"
                        variant="subtle"
                        color={copied ? "teal" : "gray"}
                        onClick={copy}
                      >
                        {copied ? (
                          <IconCheck size={16} />
                        ) : (
                          <IconCopy size={16} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </Paper>

            <Group w="100%" grow>
              <Button
                leftSection={<IconRefresh size={16} />}
                onClick={this.retry}
              >
                Tekrar Dene
              </Button>
              <Button
                variant="default"
                leftSection={<IconReload size={16} />}
                onClick={() => window.location.reload()}
              >
                Sayfayı Yenile
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Center>
    );
  }
}
